import { useCallback, useEffect, useState } from 'react'

import { ApiError, authApi, type SmsPurpose } from '@/api'
import { isValidCnMobile } from '@/features/auth/lib/phone'

const COOLDOWN_SEC = 60

export function useSmsCode(purpose: SmsPurpose) {
  const [cooldown, setCooldown] = useState(0)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = window.setTimeout(() => setCooldown((s) => s - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [cooldown])

  const send = useCallback(
    async (phone: string, captchaVerifyParam?: string, inviteCode?: string) => {
      const trimmed = phone.trim()
      if (!trimmed) {
        throw new Error('phone_required')
      }
      if (!isValidCnMobile(trimmed)) {
        throw new Error('phone_invalid')
      }
      setSending(true)
      try {
        const invite = inviteCode?.trim()
        const res = await authApi.sendSmsCode({
          phone: trimmed,
          purpose,
          invite_code: invite || undefined,
          captcha_verify_param: captchaVerifyParam,
        })
        setCooldown(COOLDOWN_SEC)
        return res
      } finally {
        setSending(false)
      }
    },
    [purpose],
  )

  return { send, sending, cooldown, canSend: cooldown <= 0 && !sending }
}

export function smsErrorMessage(err: unknown, t: (key: string) => string): string {
  if (err instanceof Error && err.message === 'phone_required') {
    return t('auth.sms.phoneRequired')
  }
  if (err instanceof Error && err.message === 'phone_invalid') {
    return t('auth.sms.phoneInvalid')
  }
  if (err instanceof Error && err.message === 'invite_required') {
    return t('auth.tenantInvite.required')
  }
  if (err instanceof ApiError) {
    if (err.code === 40002) return t('auth.sms.phoneInvalid')
    if (err.code === 40400 || err.code === 40401) return t('auth.sms.phoneNotRegistered')
    if (err.code === 40092) return t('auth.tenantInvite.invalid')
    if (err.code === 42902) return t('auth.sms.tooFrequent')
    if (err.code === 42903) return t('auth.sms.dailyLimit')
    if (err.code === 42901) return err.message || t('auth.registerPage.failed')
    if (err.code === 40010) return t('auth.captcha.required')
    if (err.code === 40011) {
      if (/\(F\d{3}\)/.test(err.message)) return err.message
      return t('auth.captcha.failed')
    }
    if (err.code === 50201) {
      if (/SMS_TEMPLATE_ILLEGAL|模板/.test(err.message)) {
        return t('auth.sms.templateMismatch')
      }
      return t('auth.sms.providerFailed')
    }
    if (err.code >= 50000 || err.status >= 500) {
      return t('auth.sms.sendFailed')
    }
    return err.message || t('auth.sms.sendFailed')
  }
  return t('auth.loginPage.networkError')
}
